<template>
  <div class="stats-grid">
    <div class="stats-container">
      <div class="stat-card total">
        <div class="stat-icon">
          <i class="fa-solid fa-calendar-check"></i>
          <div class="stat-pulse"></div>
        </div>
        <div class="stat-info">
          <h3>{{ upcomingMatchesCount }}</h3>
          <p>Upcoming Matches</p>
          <div class="stat-trend">
            <i class="fa-solid fa-arrow-up"></i>
            <span>{{ upcomingMatchRate }}% this month</span>
          </div>
        </div>
        <div class="stat-wave"></div>
      </div>
      <div class="stat-card confirmed">
        <div class="stat-icon">
          <i class="fa-solid fa-trophy"></i>
          <div class="stat-pulse"></div>
        </div>
        <div class="stat-info">
          <h3>{{ winRate }}%</h3>
          <p>Win Rate</p>
          <div class="stat-trend" :class="winTrend">
            <i :class="winTrendIcon"></i>
            <span>{{ winTrendText }}</span>
          </div>
        </div>
        <div class="stat-wave"></div>
      </div>
      <div class="stat-card pending">
        <div class="stat-icon">
          <i class="fa-solid fa-users"></i>
          <div class="stat-pulse"></div>
        </div>
        <div class="stat-info">
          <h3>{{ activePlayersCount }}</h3>
          <p>Active Players</p>
          <div class="stat-trend">
            <i class="fa-solid fa-user-plus"></i>
            <span>{{ newPlayersThisMonth }} new this month</span>
          </div>
        </div>
        <div class="stat-wave"></div>
      </div>
      <div class="stat-card revenue">
        <div class="stat-icon">
          <i class="fa-solid fa-star"></i>
          <div class="stat-pulse"></div>
        </div>
        <div class="stat-info">
          <h3>{{ teamRating }}/5</h3>
          <p>Team Rating</p>
          <div class="stat-trend">
            <i class="fa-solid fa-chart-line"></i>
            <span>Based on {{ ratingCount }} reviews</span>
          </div>
        </div>
        <div class="stat-wave"></div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'StatsCards',
  props: {
    upcomingMatchesCount: {
      type: Number,
      default: 0
    },
    upcomingMatchRate: {
      type: Number,
      default: 0
    },
    winRate: {
      type: Number,
      default: 0
    },
    winTrend: {
      type: String,
      default: 'positive'
    },
    winTrendIcon: {
      type: String,
      default: 'fa-solid fa-arrow-up'
    },
    winTrendText: {
      type: String,
      default: ''
    },
    activePlayersCount: {
      type: Number,
      default: 0
    },
    newPlayersThisMonth: {
      type: Number,
      default: 0
    },
    teamRating: {
      type: [String, Number],
      default: 'N/A'
    },
    ratingCount: {
      type: Number,
      default: 0
    }
  }
};
</script>

<style scoped>
.stats-grid {
  margin-bottom: 40px;
}

.stats-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 24px;
}

.stat-card {
  background: var(--card-bg);
  border-radius: 20px;
  padding: 25px;
  display: flex;
  align-items: center;
  gap: 20px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.stat-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  border-color: var(--card-hover-border);
}

.stat-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: inherit;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.stat-card:hover::before {
  opacity: 1;
}

.stat-card.total { border-left: 4px solid #667eea; }
.stat-card.pending { border-left: 4px solid var(--pending-border); }
.stat-card.confirmed { border-left: 4px solid var(--approved-border); }
.stat-card.revenue { border-left: 4px solid #9C27B0; }

.stat-icon {
  position: relative;
  width: 60px;
  height: 60px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  z-index: 1;
}

.stat-card.total .stat-icon { background: linear-gradient(135deg, #667eea, #764ba2); }
.stat-card.pending .stat-icon { background: linear-gradient(135deg, #ffa726, #fb8c00); }
.stat-card.confirmed .stat-icon { background: linear-gradient(135deg, #4CAF50, #45a049); }
.stat-card.revenue .stat-icon { background: linear-gradient(135deg, #9C27B0, #7B1FA2); }

.stat-pulse {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 16px;
  animation: pulse 2s infinite;
}

.stat-info h3 {
  font-size: 2rem;
  margin: 0;
  color: var(--heading-color);
  font-weight: 800;
}

.stat-info p {
  margin: 5px 0 0 0;
  color: var(--subtext-color);
  font-weight: 500;
}

.stat-trend {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.85rem;
  margin-top: 8px;
}

.stat-trend.positive {
  color: var(--approved-color);
}

.stat-trend.negative {
  color: var(--rejected-color);
}

.stat-wave {
  position: absolute;
  bottom: -10px;
  right: -10px;
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 50%;
}

@keyframes pulse {
  0% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.1); opacity: 0.2; }
  100% { transform: scale(1); opacity: 0.5; }
}
</style>
